// adminRbac.ts — single source of truth for admin role labels + the permission matrix.
// Imported by StaffPermissionsTab (RBAC reference) and AdminProfilePage ("Your access").
// NOTE: this is a FRONTEND REFERENCE of documented policy. The backend authorizers
// (requireAdminOrSuperAdmin / requireSuperAdmin) are the real enforcement boundary.
// When functional roles land (#361), extend PermissionRow with new columns here and
// both consumers update automatically.

export type AdminRole = 'admin' | 'super_admin';

export const roleBadgeColor = (role: string): 'error' | 'warning' | 'default' => {
  if (role === 'super_admin') return 'error';
  if (role === 'admin') return 'warning';
  return 'default';
};

export const roleLabel = (role: string): string => {
  if (role === 'super_admin') return 'Super Admin';
  if (role === 'admin') return 'Admin';
  return role;
};

export interface PermissionRow {
  action: string;
  user: boolean;
  admin: boolean;
  superAdmin: boolean;
}

// ── RBAC Permission Matrix (static reference) ────────────────────────────────
export const PERMISSIONS: PermissionRow[] = [
  { action: 'View user profiles',       user: false, admin: true,  superAdmin: true },
  { action: 'Search users',             user: false, admin: true,  superAdmin: true },
  { action: 'Suspend / activate users', user: false, admin: false, superAdmin: true },
  { action: 'Edit user roles',          user: false, admin: false, superAdmin: true },
  { action: 'View platform analytics',  user: false, admin: true,  superAdmin: true },
  { action: 'Manage feature flags',     user: false, admin: true,  superAdmin: true },
  { action: 'Send broadcasts',          user: false, admin: true,  superAdmin: true },
  { action: 'Manage staff accounts',    user: false, admin: false, superAdmin: true },
  { action: 'View audit logs',          user: false, admin: true,  superAdmin: true },
  { action: 'Manage AI operations',     user: false, admin: true,  superAdmin: true },
  { action: 'Delete user accounts',     user: false, admin: false, superAdmin: true },
  { action: 'Access financial data',    user: false, admin: false, superAdmin: true },
];

// Whether a given role is granted a permission row.
export const roleHasPermission = (row: PermissionRow, role: string): boolean => {
  if (role === 'super_admin') return row.superAdmin;
  if (role === 'admin') return row.admin;
  return row.user;
};

// Split the matrix into granted / withheld for a role (drives the profile "Your access" view).
export const capabilitiesForRole = (role: string): { granted: string[]; withheld: string[] } => {
  const granted: string[] = [];
  const withheld: string[] = [];
  for (const row of PERMISSIONS) {
    (roleHasPermission(row, role) ? granted : withheld).push(row.action);
  }
  return { granted, withheld };
};